import cx from 'classnames'
import { useRef } from 'react'
import { useLocation } from 'react-router-dom'

import Styles from './PriceIncreaseMessage.module.scss'

import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { pluralize } from 'utils'
import { Link } from 'react-router-dom'
import { Text } from 'blaze-ui/Text/Text'

export const MONTHLY_ADDITIONAL_SEAT_PRICE = 25
export const YEARLY_ADDITIONAL_SEAT_PRICE = 20
export const MONTHLY_TEAM_PRICE = 59
export const YEARLY_TEAM_PRICE = 47
export const BUSINESS_PLAN_MEMBER_MINIMUM = 20
export const TEAM_PLAN_MEMBER_LIMIT = 3

interface Props {
  currentPlan: PaymentPlanKey
  newMemberCount: number
  isBillingMonthly: boolean
}

export const PriceIncreaseMessage = ({ currentPlan, newMemberCount, isBillingMonthly }: Props) => {
  const workspace = useCurrentWorkspace()
  const location = useLocation()
  const membersCount = useRef(workspace?.members_count || 0)

  const costPerAdditionalSeat = isBillingMonthly ? MONTHLY_ADDITIONAL_SEAT_PRICE : YEARLY_ADDITIONAL_SEAT_PRICE
  const memberLimit = currentPlan === PaymentPlanKey.BlazeTeam ? TEAM_PLAN_MEMBER_LIMIT : 1
  const monthlyIncrease =
    costPerAdditionalSeat * Math.max(0, newMemberCount - Math.max(0, memberLimit - membersCount.current))

  if (currentPlan === PaymentPlanKey.BlazeCreator) {
    return (
      <div className={Styles.textContainer}>
        <Text>
          Adding <span className={Styles.bold}>{newMemberCount}</span> extra {pluralize('member', newMemberCount)} will
          upgrade your workspace to <span className={Styles.bold}>Team Plan</span> and increase your monthly price by{' '}
          <span className={Styles.bold}>${monthlyIncrease}/mo</span>.
        </Text>
        <Text>
          Go to <Link to={`${location.pathname}?settings=billing`}>Billing</Link> in Settings to manage your plan.
        </Text>
      </div>
    )
  }

  if (
    currentPlan === PaymentPlanKey.BlazeTeam &&
    membersCount.current + newMemberCount < BUSINESS_PLAN_MEMBER_MINIMUM
  ) {
    return (
      <div className={Styles.textContainer}>
        <Text>
          Adding <span className={Styles.bold}>{newMemberCount}</span> extra {pluralize('member', newMemberCount)} to
          your workspace will increase your monthly price by{' '}
          <span className={Styles.bold}>
            ${monthlyIncrease}
            /mo
          </span>
          .
        </Text>
        <Text>
          Go to <Link to={`${location.pathname}?settings=billing`}>Billing</Link> in Settings to manage your plan.
        </Text>
      </div>
    )
  }

  if (
    currentPlan === PaymentPlanKey.BlazeTeam &&
    membersCount.current + newMemberCount >= BUSINESS_PLAN_MEMBER_MINIMUM
  ) {
    return (
      <div className={cx(Styles.textContainer, Styles.tall)}>
        <Text>
          Adding <span className={Styles.bold}>{newMemberCount}</span> extra {pluralize('member', newMemberCount)} to
          your workspace will increase your monthly price by <span className={Styles.bold}>${monthlyIncrease}/mo.</span>
        </Text>
        <Text>
          With {BUSINESS_PLAN_MEMBER_MINIMUM}+ seats, you qualify for our{' '}
          <span className={Styles.bold}>Enterprise Plan</span>, which comes with a discount package.{' '}
          <a href="mailto:support@blaze.ai">Contact us</a> to upgrade or go to{' '}
          <Link to={`${location.pathname}?settings=billing`}>Billing</Link> in Settings to manage your plan.
        </Text>
      </div>
    )
  }

  return null
}
